import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import { Button } from '../primitives/button';
import { cn } from '../lib/utils';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  icon?: LucideIcon;
  loading?: boolean;
  loadingLabel?: string;
}

interface EmptyStateProps extends React.ComponentPropsWithoutRef<'div'> {
  icon?: LucideIcon;
  title: string;
  message?: string;
  action?: EmptyStateAction;
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
}

export function EmptyState({
  icon: Icon = Inbox,
  title,
  message,
  action,
  size = 'md',
  disabled = false,
  className,
  ...props
}: EmptyStateProps) {
  const sizes = {
    sm: {
      container: 'py-8',
      iconWrapper: 'mb-3 size-10',
      icon: 'size-5',
      title: 'text-sm font-semibold',
      message: 'text-sm',
    },
    md: {
      container: 'py-12',
      iconWrapper: 'mb-4 size-12',
      icon: 'size-6',
      title: 'text-section-title',
      message: 'text-sm',
    },
    lg: {
      container: 'py-16',
      iconWrapper: 'mb-5 size-14',
      icon: 'size-7',
      title: 'text-xl font-semibold',
      message: 'text-sm',
    },
  } as const;

  const style = sizes[size];
  const ActionIcon = action?.icon;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        style.container,
        className
      )}
      {...props}
    >
      <div className={cn('flex items-center justify-center rounded-full bg-muted', style.iconWrapper)}>
        <Icon className={cn('text-muted-foreground', style.icon)} />
      </div>
      <h2 className={cn('text-foreground', style.title)}>{title}</h2>
      {message ? <p className={cn('mt-2 max-w-md text-muted-foreground', style.message)}>{message}</p> : null}
      {action && !disabled ? (
        <Button className="mt-5" onClick={action.onClick} loading={action.loading}>
          {ActionIcon && !action.loading ? <ActionIcon /> : null}
          {action.loading ? action.loadingLabel || action.label : action.label}
        </Button>
      ) : null}
    </div>
  );
}
